import { NGROK } from "../../../../APIs";
import api, { apiClient } from "../../../../utils/apiClient";


export interface RawAiLogEvent {
    result?: string;
    table?: number;
    _start?: string;
    _stop?: string;
    _time?: string;
    _value?: string;
    _field?: string;
    _measurement?: string;

    computerId?: string;
    computerName?: string;
    computerUserEmail?: string;

    message?: string;

    resourceName?: string;
    resourceType?: string;

    tenantName?: string;

    id?: string;
    requestId?: string;

    timestamp?: string;

    eventType?: string;
    eventName?: string;
    type?: string;

    aiEvent?: string;

    actor?: string;
    user?: string;

    action?: string;
    resource?: string;

    status?: string;

    eventJson?: string | object;

    details?: Record<string, any>;
    metadata?: Record<string, any>;

    [key: string]: unknown;
}


export interface AiLogEvent {
    id: string;
    timestamp: string;
    eventType: string;
    actor: string;
    action: string;
    resource: string;
    result: string;
    severity?: string;
    metadata?: any;
    details?: Record<string, any>;
    rawEvent?: RawAiLogEvent;
}

export interface AiLogQueryOptions {
    computerName?: string;
    computerId?: string;
    computeremail?: string;
    customStartDate?: string;
    customEndDate?: string;
    cursor?: string;
    limit?: number;
    searchQuery?: string;
    eventTypes?: string[];
    actors?: string[];
    typeFilter?: string;
    agentFilter?: string;
    jsonFilter?: string;
}

export interface BurstGroup {
    burstId: string;
    burstStartTime: string;
    burstEndTime: string;
    eventCount: number;
    events: RawAiLogEvent[];
}

export interface AiLogResponse {
    bursts: BurstGroup[];
    nextCursor: string | null;
    hasMore: boolean;
}

export interface AiLogStats {
    totalLogs: number;
    uniqueActors: number;
    agents: string[];
    eventTypeDistribution: Record<string, number>;
    timeRange: {
        start: string;
        end: string;
    };
}

export const getAgenticAiLogs = async (
    email: string,
    tenantName: string,
    filterPagination: string,
    isGlobal?: boolean,
    options?: AiLogQueryOptions
): Promise<AiLogResponse | undefined> => {
    try {
        if (!email || !tenantName) {
            console.error("Email or Tenant Name is missing");
            return undefined;
        }

        let url = `${NGROK}/api/influxdb/agentic-ai-logs?timingMode=${encodeURIComponent(filterPagination)}&email=${encodeURIComponent(email)}&tenantName=${encodeURIComponent(tenantName)}`;

        const cursor = options?.cursor;
        const limit = options?.limit ?? 8;
        url += `&limit=${limit}`;
        if (cursor) {
            url += `&cursor=${encodeURIComponent(cursor)}`;
        }

        if (options?.searchQuery) {
            url += `&searchParams=${encodeURIComponent(options.searchQuery)}`;
        }

        if (options?.customStartDate && options?.customEndDate) {
            url += `&customDateMode=true&customStartDate=${encodeURIComponent(options.customStartDate)}&customEndDate=${encodeURIComponent(options.customEndDate)}`;
        }

        if (options?.computerId && !isGlobal) {
            url += `&computerId=${encodeURIComponent(options.computerId)}&isDeviceSpecific=true`;
        }
        if (options?.computeremail) {
            url += `&computer_email=${encodeURIComponent(options.computeremail)}`;
        }

        if (options?.typeFilter && options.typeFilter.trim() !== "" && options.typeFilter.trim().toLowerCase() !== "all") {
            url += `&typeFilter=${encodeURIComponent(options.typeFilter)}`;
        }
        if (options?.agentFilter) {
            url += `&agentFilter=${encodeURIComponent(options.agentFilter)}`;
        }

        if (options?.jsonFilter && options.jsonFilter.trim() !== "" && options.jsonFilter.trim().toLowerCase() !== "all") {
            url += `&jsonFilter=${encodeURIComponent(options.jsonFilter)}`;
        }

        const response = await apiClient<AiLogResponse>({
            method: "GET",
            url
        });

        return response;
    } catch (error) {
        console.error("Error fetching AI logs from agenticAilogs bucket:", error);
        return undefined;
    }
};


export const getAgenticAiLogStats = async (
    email: string,
    tenantName: string,
    filterPagination: string,
    isGlobal?: boolean,
    options?: AiLogQueryOptions
): Promise<AiLogStats | undefined> => {
    try {
        const url = `${NGROK}/api/influxdb/agentic-ai-log-stats?timingMode=${encodeURIComponent(filterPagination)}&email=${encodeURIComponent(email)}&tenantName=${encodeURIComponent(tenantName)}` +
            (options?.searchQuery ? `&searchParams=${encodeURIComponent(options.searchQuery)}` : "") +
            (options?.customStartDate && options?.customEndDate ? `&customDateMode=true&customStartDate=${encodeURIComponent(options.customStartDate)}&customEndDate=${encodeURIComponent(options.customEndDate)}` : "") +
            (options?.computerName && !isGlobal ? `&computerName=${encodeURIComponent(options.computerName)}` : "") +
            (options?.computerId && !isGlobal ? `&computerId=${encodeURIComponent(options.computerId)}&isDeviceSpecific=true` : "") +
            (options?.computeremail ? `&computer_email=${encodeURIComponent(options.computeremail)}` : "") +
            (options?.typeFilter ? `&typeFilter=${encodeURIComponent(options.typeFilter)}` : "") +
            (options?.agentFilter ? `&agentFilter=${encodeURIComponent(options.agentFilter)}` : "") +
            (options?.jsonFilter ? `&jsonFilter=${encodeURIComponent(options.jsonFilter)}` : "");

        const response = await apiClient<AiLogStats>({
            method: "GET",
            url,
        });

        return response;
    } catch (error) {
        console.error("Error fetching AI log statistics:", error);
        return undefined;
    }
};

export const exportAgenticAiLogs = async (
    email: string,
    tenantName: string,
    filterPagination: string,
    isGlobal?: boolean,
    options?: AiLogQueryOptions
): Promise<Blob | undefined> => {
    try {
        if (!email || !tenantName) {
            console.error("Email or Tenant Name is missing");
            return undefined;
        }

        let url = `${NGROK}/api/influxdb/agentic-ai-logs-export?timingMode=${encodeURIComponent(filterPagination)}&email=${encodeURIComponent(email)}&tenantName=${encodeURIComponent(tenantName)}`;

        if (options?.cursor) {
            url += `&cursor=${encodeURIComponent(options.cursor)}`;
        }

        if (options?.searchQuery) {
            url += `&searchParams=${encodeURIComponent(options.searchQuery)}`;
        }

        if (options?.customStartDate && options?.customEndDate) {
            url += `&customDateMode=true&customStartDate=${encodeURIComponent(options.customStartDate)}&customEndDate=${encodeURIComponent(options.customEndDate)}`;
        }

        if (options?.computerId && !isGlobal) {
            url += `&computerId=${encodeURIComponent(options.computerId)}&isDeviceSpecific=true`;
        }
        if (options?.computeremail) {
            url += `&computer_email=${encodeURIComponent(options.computeremail)}`;
        }
        if (options?.typeFilter) {
            url += `&typeFilter=${encodeURIComponent(options.typeFilter)}`;
        }
        if (options?.agentFilter) {
            url += `&agentFilter=${encodeURIComponent(options.agentFilter)}`;
        }
        if (options?.jsonFilter) {
            url += `&jsonFilter=${encodeURIComponent(options.jsonFilter)}`;
        }

        const response = await api.get<Blob>(url, {
            responseType: "blob",
        });

        return response.data;
    } catch (error) {
        console.error("Error exporting AI logs:", error);
        return undefined;
    }
};


export const filterAiLogsByEventType = (
    logs: AiLogEvent[],
    eventTypes: string[]
): AiLogEvent[] => {
    if (!eventTypes || eventTypes.length === 0) {
        return logs;
    }
    return logs.filter(log =>
        eventTypes.some(type =>
            log.eventType.toUpperCase() === type.toUpperCase()
        )
    );
};


export const filterAiLogsByActor = (
    logs: AiLogEvent[],
    actors: string[]
): AiLogEvent[] => {
    if (!actors || actors.length === 0) {
        return logs;
    }
    return logs.filter(log =>
        actors.some(actor =>
            log.actor.toLowerCase().includes(actor.toLowerCase())
        )
    );
};


function normalizeAiLogEvent(raw: RawAiLogEvent): AiLogEvent | null {
    try {
        const eventType = String(
            raw.aiEvent ||
            raw.resourceType ||
            raw.eventType ||
            raw.eventName ||
            raw.type ||
            "AI-UNKNOWN"
        ).toUpperCase();

        if (!eventType.startsWith("AI-")) {
            return null;
        }

        const timestamp = raw._time ||
            raw.timestamp ||
            new Date().toISOString();

        const actor = String(
            raw.computerUserEmail ||
            raw.actor ||
            raw.user ||
            "unknown"
        ).trim();

        const action = String(
            raw.message ||
            raw.action ||
            raw.eventName ||
            ""
        ).trim();

        const resource = String(
            raw.resourceName ||
            raw.resource ||
            ""
        ).trim();

        const result = String(
            raw._value ||
            raw.result ||
            raw.status ||
            "completed"
        ).trim();


        const id = String(
            raw.id ||
            `${timestamp}-${actor}-${Math.random().toString(36).substr(2, 11)}`
        );

        return {
            id,
            timestamp,
            eventType,
            actor,
            action: action || eventType,
            resource: resource || "N/A",
            result,
            severity: "info",
            metadata: {
                computerId: raw.computerId,
                computerName: raw.computerName,
                tenantName: raw.tenantName,
                table: raw.table,
                measurement: raw._measurement,
                field: raw._field,
                timeRange: {
                    start: raw._start,
                    stop: raw._stop
                }
            },
            details: {
                ...raw.metadata,
                ...raw.details
            },
            rawEvent: raw,
        };
    } catch (error) {
        console.warn("Failed to normalize AI log event:", error, raw);
        return null;
    }
}


export const getFilteredAgenticAiLogs = async (
    email: string,
    tenantName: string,
    filterPagination: string,
    options?: AiLogQueryOptions
): Promise<AiLogEvent[] | undefined> => {
    try {
        const response = await getAgenticAiLogs(
            email,
            tenantName,
            filterPagination,
            false,
            options
        );

        if (!response || !response.bursts) {
            return undefined;
        }

        // Flatten all burst events
        const rawEvents: RawAiLogEvent[] = response.bursts.flatMap(
            (burst) => burst.events || []
        );

        // Normalize events
        let logs: AiLogEvent[] = rawEvents
            .map(normalizeAiLogEvent)
            .filter((log): log is AiLogEvent => log !== null);

        // Filter by event types
        if (options?.eventTypes?.length) {
            logs = filterAiLogsByEventType(logs, options.eventTypes);
        }

        // Filter by actors
        if (options?.actors?.length) {
            logs = filterAiLogsByActor(logs, options.actors);
        }

        // Search filter
        if (options?.searchQuery) {
            const query = options.searchQuery.toLowerCase();

            logs = logs.filter((log) =>
                log.action.toLowerCase().includes(query) ||
                log.resource.toLowerCase().includes(query) ||
                log.eventType.toLowerCase().includes(query) ||
                log.actor.toLowerCase().includes(query)
            );
        }

        return logs;
    } catch (error) {
        console.error("Error fetching filtered AI logs:", error);
        return undefined;
    }
};


export const summarizeAiEvents = async (
    eventMessages: string[]
): Promise<string | undefined> => {
    try {
        if (!eventMessages || eventMessages.length === 0) {
            console.warn("No event messages provided for summarization");
            return undefined;
        }

        const response = await apiClient<string[]>({
            method: "POST",
            url: `${NGROK}/api/llm/log-summary`,
            data: eventMessages,
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (Array.isArray(response) && response.length > 0) {
            return response.join("\n");
        }

        console.warn("No summary returned from LLM API");
        return undefined;
    } catch (error: any) {
        console.error("Error summarizing AI events:", error);

        throw new Error(
            error.response?.data?.message ||
            error.message ||
            "Failed to generate summary"
        );
    }
};
